import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {Users,Shield,CreditCard,Banknote,TrendingUp,AlertTriangle,DollarSign,ChevronRight,Activity,Search,UserCheck,Wallet,Star,BarChart2,ArrowUpRight,ArrowDownRight,
  BadgeCheck,Mail,MapPin,FileText,CheckCheck,} from "lucide-react";
import {AreaChart,Area,BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,Legend,} from "recharts";
import axiosInstance from "../../../api/axiosInstance";

/* ─── Helpers ─────────────────────────────────────────────────── */

const fmtINR  = (n)   => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (iso, opts = { day: "numeric", month: "short", year: "numeric" }) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", opts) : "—";

/**
 * Build weekly user-growth data from a list of users.
 * Groups by ISO week label (e.g. "W1 Mar") in chronological order.
 */
function buildUserGrowthData(users) {
  if (!users.length) return [];
  const sorted = [...users].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const weekMap = new Map();
  sorted.forEach((u) => {
    const d   = new Date(u.created_at);
    const day = d.getDate();
    const wk  = Math.ceil(day / 7);
    const key = `W${wk} ${d.toLocaleString("en-IN", { month: "short" })}`;
    weekMap.set(key, (weekMap.get(key) || 0) + 1);
  });
  // convert to cumulative totals
  let cumulative = 0;
  return Array.from(weekMap.entries()).map(([week, count]) => {
    cumulative += count;
    return { week, users: cumulative };
  });
}

/**
 * Build payment-volume bar data grouped by date.
 * Only uses approved payments.
 */
function buildPaymentVolumeData(payments) {
  const approved = payments.filter((p) => p.status === "approved");
  if (!approved.length) return [];
  const dateMap = new Map();
  approved.forEach((p) => {
    const label = fmtDate(p.submitted_at, { day: "numeric", month: "short" });
    const entry = dateMap.get(label) ?? { month: label, joining: 0, installment: 0 };
    if (p.type === "joining")     entry.joining     += parseFloat(p.amount);
    else                          entry.installment += parseFloat(p.amount);
    dateMap.set(label, entry);
  });
  return Array.from(dateMap.values());
}

/* ─── Status colours ──────────────────────────────────────────── */
const statusColors = {
  approved:  "text-emerald-600 bg-emerald-50 border-emerald-200",
  pending:   "text-amber-600 bg-amber-50 border-amber-200",
  rejected:  "text-red-600 bg-red-50 border-red-200",
  active:    "text-emerald-600 bg-emerald-50 border-emerald-200",
  inactive:  "text-gray-500 bg-gray-50 border-gray-200",
  none:      "text-gray-400 bg-gray-50 border-gray-200",
  completed: "text-violet-600 bg-violet-50 border-violet-200",
};

/* ─── Shared small components ─────────────────────────────────── */

function Badge({ status, label }) {
  const cls = statusColors[status] ?? "text-gray-600 bg-gray-50 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls} capitalize`}>
      {label ?? status}
    </span>
  );
}

function StatCard({ icon, label, value, sub, accentClass, delta, positive }) {
    return (
      <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-3.5 sm:p-4 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
        
        {/* 1. Top Row: Label & Icon */}
        <div className="flex justify-between items-start mb-1.5">
          <span className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">
            {label}
          </span>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${accentClass}`}>
            {icon}
          </div>
        </div>
  
        {/* 2. Middle Row: Value & Delta */}
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight leading-none">
            {value}
          </span>
          {delta && (
            <span className={`hidden sm:inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-gray-50 dark:bg-white/5 ${positive ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
              {delta}
            </span>
          )}
        </div>
  
        {/* 3. Bottom Row: Subtext (No divider line to save vertical space) */}
        {sub && (
          <div className="hidden sm:block text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 truncate">
            {sub}
          </div>
        )}
        
      </div>
    );
  }

function SectionHeading({ icon, title, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <span className="text-primary">{icon}</span>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
      </div>
      {action}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function ActionBtn({ label, variant = "approve" }) {
  const cls =
    variant === "approve"
      ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
      : variant === "reject"
      ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
      : "bg-secondary border-border text-secondary-foreground hover:bg-muted";
  return (
    <button className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition-colors ${cls}`}>
      {label}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════ */
const AdminDashboard = () => {

  /* ── UI state ── */
  const [userSearch, setUserSearch] = useState("");
  const [payTab,     setPayTab]     = useState("all");  // "all" | "pending" | "approved"
  const [kycTab,     setKycTab]     = useState("all");  // "all" | "pending" | "approved"

  /* ── Data state ── */
  const [data,      setData]      = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error,     setError]     = useState(null);

  /* ── Profile image ── */
  const [profileImg, setProfileImg] = useState(null);
  const [imgError,   setImgError]   = useState(false);

  /* ── 1. Fetch all dashboard data in parallel ── */
  useEffect(() => {
    const load = async () => {
      try {
        const results = await Promise.allSettled([
          axiosInstance.get("/profile/get"),                                    // 0  admin profile
          axiosInstance.get("/commission/get"),                                 // 1  earnings
          axiosInstance.get("/admin/user/all?page=1&limit=100&status=all"),     // 2  users
          axiosInstance.get("/admin/pay/all?status=all&page=1&limit=100"),      // 3  payments
          axiosInstance.get("/admin/kyc/all?status=all&page=1&limit=100"),      // 4  kyc list
          axiosInstance.get("/admin/bank/all?status=all&page=1&limit=100"),     // 5  bank list
        ]);

        const ok  = (i) => results[i].status === "fulfilled";
        const val = (i, fb) => (ok(i) ? results[i].value.data : fb);

        const rawProfile    = val(0, {});
        const rawCommission = val(1, {});
        const rawUsers      = val(2, { data: [] });
        const rawPayments   = val(3, { data: [] });
        const rawKyc        = val(4, { data: [] });
        const rawBank       = val(5, { data: [] });

        // each paginated endpoint wraps records under `.data`
        const users    = Array.isArray(rawUsers.data)    ? rawUsers.data    : [];
        const payments = Array.isArray(rawPayments.data) ? rawPayments.data : [];
        const kycList  = Array.isArray(rawKyc.data)      ? rawKyc.data      : [];
        const bankList = Array.isArray(rawBank.data)     ? rawBank.data     : [];

        setData({
          profile: {
            id:         rawProfile.id         || "—",
            name:       rawProfile.name       || "Admin",
            email:      rawProfile.email      || "—",
            phone:      rawProfile.phone      || "—",
            city:       rawProfile.city       || "—",
            state:      rawProfile.state      || "—",
            kyc:        rawProfile.kyc        || "—",
            membership: rawProfile.membership || "—",
            created_at: rawProfile.created_at || null,
          },
          earnings: {
            earned:    Number(rawCommission.earned    || 0),
            redeemed:  Number(rawCommission.redeemed  || 0),
            balance:   Number(rawCommission.balance   || 0),
            forfeited: Number(rawCommission.forfeited || 0),
          },
          users,
          payments,
          kycList,
          bankList,
          /* derived chart data */
          userGrowthData:    buildUserGrowthData(users),
          paymentVolumeData: buildPaymentVolumeData(payments),
        });
      } catch (err) {
        console.error("AdminDashboard Error:", err);
        setError("Failed to load dashboard data.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  /* ── 2. Fetch profile image once we have the admin ID ── */
  useEffect(() => {
    if (!data?.profile?.id) return;
    let objectUrl = null;
    axiosInstance
      .get(`/profile/get-image/${data.profile.id}`, { responseType: "blob" })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setProfileImg(objectUrl);
      })
      .catch(() => setImgError(true));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [data?.profile?.id]);

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-9 h-9 text-primary animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <p className="text-sm text-muted-foreground">Loading admin dashboard…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error || !data) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-4">{error || "Something went wrong."}</p>
          <button onClick={() => window.location.reload()} className="px-5 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-semibold">
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* ── Destructure for convenience ── */
  const {
    profile, earnings, users, payments, kycList, bankList,
    userGrowthData, paymentVolumeData,
  } = data;

  /* ── Derived counts ── */
  const totalRevenue    = payments.filter((p) => p.status === "approved").reduce((s, p) => s + parseFloat(p.amount), 0);
  const pendingPayments = payments.filter((p) => p.status === "pending").length;
  const pendingKyc      = kycList.filter((k)  => k.status === "pending").length;
  const pendingBank     = bankList.filter((b)  => b.status === "pending").length;

  /* ── Filtered lists ── */
  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.id.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredPayments =
    payTab === "all"
      ? payments
      : payments.filter((p) => p.status === (payTab === "approved" ? "approved" : "pending"));

  const filteredKyc =
    kycTab === "all"
      ? kycList
      : kycList.filter((k) => k.status === (kycTab === "approved" ? "approved" : "pending"));

  /* ── SVG donut geometry for Revenue ring ── */
  const R_REV    = 64;
  const C_REV    = 2 * Math.PI * R_REV;
  const revRatio = Math.min(totalRevenue / Math.max(totalRevenue * 1.5, 1), 1);
  const comRatio = Math.min(earnings.balance / Math.max(totalRevenue * 1.5, 1), 1);

  return (
    <div className="min-h-screen bg-background">
      {/* Admin top accent */}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

        {/* ══ Admin Hero Banner ══ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-700 text-white p-6 mb-8 shadow-xl">
          <div className="absolute right-0 top-0 h-full w-80 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-10 w-40 h-40 rounded-full border-4 border-white" />
            <div className="absolute top-16 right-24 w-56 h-56 rounded-full border-2 border-white" />
          </div>

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            {/* avatar */}
            {/* avatar */}
            <div className="w-20 h-20 rounded-full bg-white/20 border border-white/30 overflow-hidden flex items-center justify-center text-3xl font-bold shrink-0">
            {profileImg && !imgError ? (
                <img
                src={profileImg}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={() => setImgError(true)}
                />
            ) : (
                // Fallback: Use the first letter of the name
                profile.name.charAt(0)
            )}
            </div>

            <div className="flex-1">
              <div className="text-sm text-white/70 mb-0.5">Admin Portal</div>
              <h1 className="text-2xl font-bold">{profile.name}</h1>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> KYC {profile.kyc}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1 capitalize">
                  <Star size={10} /> {profile.membership}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1">
                  <MapPin size={10} /> {profile.city}, {profile.state}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 border border-white/30 rounded-full px-3 py-1">
                  <Mail size={10} /> {profile.email}
                </span>
              </div>
            </div>

            <div className="text-right hidden md:block shrink-0">
              <div className="text-white/60 text-xs mb-1">Admin ID</div>
              <div className="font-mono text-sm font-semibold bg-white/10 rounded-lg px-3 py-1.5">{profile.id}</div>
              {profile.created_at && (
                <div className="text-white/50 text-xs mt-2">
                  Since {fmtDate(profile.created_at)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ Pending Alerts Row ══ */}
        {(pendingPayments > 0 || pendingKyc > 0 || pendingBank > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {pendingPayments > 0 && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                <AlertTriangle size={16} className="text-amber-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-800">
                    {pendingPayments} Payment{pendingPayments > 1 ? "s" : ""} Pending
                  </div>
                  <div className="text-xs text-amber-600">Awaiting verification</div>
                </div>
                <button className="text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded-lg px-2.5 py-1 hover:bg-amber-200 transition-colors shrink-0">
                  Review
                </button>
              </div>
            )}
            {pendingKyc > 0 && (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3.5">
                <Shield size={16} className="text-blue-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-800">{pendingKyc} KYC Pending</div>
                  <div className="text-xs text-blue-600">Documents to verify</div>
                </div>
                <button className="text-xs font-semibold text-blue-700 bg-blue-100 border border-blue-300 rounded-lg px-2.5 py-1 hover:bg-blue-200 transition-colors shrink-0">
                  Review
                </button>
              </div>
            )}
            {pendingBank > 0 && (
              <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl p-3.5">
                <Banknote size={16} className="text-violet-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-violet-800">{pendingBank} Bank Pending</div>
                  <div className="text-xs text-violet-600">Account verification needed</div>
                </div>
                <button className="text-xs font-semibold text-violet-700 bg-violet-100 border border-violet-300 rounded-lg px-2.5 py-1 hover:bg-violet-200 transition-colors shrink-0">
                  Review
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ Asymmetric Two-Column Layout ══ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">

          {/* ════ LEFT COLUMN ════ */}
          <div className="flex flex-col gap-6">

            {/* Primary Stat Cards */}
            <div className="max-w-5xl grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2.5">
                <StatCard
                    icon={<Users size={18} />}
                    label="Total Members"
                    value={users.length}
                    accentClass="bg-indigo-100 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400"
                    delta="New"
                    positive={true}
                    sub="Across all levels"
                />
                <StatCard
                    icon={<DollarSign size={18} />}
                    label="Total Revenue"
                    value={`₹${fmtINR(totalRevenue)}`}
                    accentClass="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
                    delta="+5k"
                    positive={true}
                    sub="Verified payments only"
                />
                <StatCard
                    icon={<CreditCard size={18} />}
                    label="Total Payments"
                    value={payments.length}
                    accentClass="bg-violet-100 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400"
                    sub={`${pendingPayments} pending approval`}
                />
                <StatCard
                    icon={<Wallet size={18} />}
                    label="Commission"
                    value={`₹${fmtINR(earnings.balance)}`}
                    accentClass="bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-500/10 dark:text-fuchsia-400"
                    sub="Available balance"
                />
                </div>

                {/* Secondary Stat Cards */}
                <div className="max-w-5xl grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <StatCard
                    icon={<Shield size={18} />}
                    label="KYC Approved"
                    value={kycList.filter((k) => k.status === "approved").length}
                    accentClass="bg-teal-100 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400"
                    sub={`${pendingKyc} pending`}
                />
                <StatCard
                    icon={<Banknote size={18} />}
                    label="Bank Verified"
                    value={bankList.filter((b) => b.status === "approved").length}
                    accentClass="bg-blue-100 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400"
                    sub={`${pendingBank} pending`}
                />
                <StatCard
                    icon={<UserCheck size={18} />}
                    label="Active Users"
                    value={users.filter((u) => u.status === "active").length}
                    accentClass="bg-green-100 text-green-600 dark:bg-green-500/10 dark:text-green-400"
                    sub="Currently active"
                />
                <StatCard
                    icon={<TrendingUp size={18} />}
                    label="Total Installments"
                    value={users.reduce((s, u) => s + (u.installment || 0), 0)}
                    accentClass="bg-orange-100 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400"
                    sub="Collected this cycle"
                />
                </div>

           {/* Charts Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                {/* Member Growth area chart */}
                <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
                <SectionHeading icon={<TrendingUp size={16} />} title="Member Growth" />
                <div className="h-48">
                    {userGrowthData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={userGrowthData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                        <defs>
                            <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#6d5cf7" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#6d5cf7" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#9ca3af" strokeOpacity={0.2} />
                        <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
                        <Tooltip
                            contentStyle={{
                            backgroundColor: "#1f2937", 
                            borderColor: "#374151", 
                            borderRadius: 8, 
                            fontSize: 12,
                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)"
                            }}
                            itemStyle={{ color: "#f9fafb", fontWeight: "bold" }}
                            labelStyle={{ color: "#9ca3af", marginBottom: "4px" }}
                            formatter={(v) => [v, "Members"]}
                        />
                        <Area type="monotone" dataKey="users" stroke="#6d5cf7" strokeWidth={2.5} fill="url(#userGrad)" dot={{ fill: "#6d5cf7", r: 4 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                    ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">No data yet.</div>
                    )}
                </div>
                </div>

                {/* Payment Volume bar chart */}
                <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
                <SectionHeading icon={<BarChart2 size={16} />} title="Payment Volume" />
                <div className="h-48">
                    {paymentVolumeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={paymentVolumeData} barSize={14} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#9ca3af" strokeOpacity={0.2} />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#9ca3af" }} />
                        <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} />
                        <Tooltip
                            contentStyle={{
                            backgroundColor: "#1f2937", 
                            borderColor: "#374151", 
                            borderRadius: 8, 
                            fontSize: 12,
                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)"
                            }}
                            itemStyle={{ color: "#f9fafb", fontWeight: "bold" }}
                            labelStyle={{ color: "#9ca3af", marginBottom: "4px" }}
                            formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, ""]}
                        />
                        <Legend 
                            iconSize={8} 
                            wrapperStyle={{ fontSize: 11, paddingTop: "10px" }} 
                            formatter={(value) => <span className="text-gray-600 dark:text-gray-400 font-medium">{value}</span>}
                        />
                        <Bar dataKey="joining"     name="Joining"     fill="#6d5cf7" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="installment" name="Installment" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">No data yet.</div>
                    )}
                </div>
                </div>

                </div>

            {/* ── Recent Members Preview Table ── */}
                <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl shadow-md dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] overflow-hidden flex flex-col">
                <div className="p-5 pb-0">
                    <SectionHeading
                    icon={<Users size={16} />}
                    title="Recently Added Members"
                    action={
                        <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search members..."
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            className="text-xs pl-7 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30 w-44 transition-colors"
                        />
                        </div>
                    }
                    />
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5">
                        <th className="text-left py-2.5 px-5 font-medium text-gray-500 dark:text-gray-400 text-xs">Member</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">ID</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">KYC</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Bank</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Status</th>
                        <th className="text-left py-2.5 px-5 font-medium text-gray-500 dark:text-gray-400 text-xs">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length > 0 ? (
                        filteredUsers
                            // 1. Sort by newest created_at date
                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                            // 2. Take only the top 5
                            .slice(0, 5)
                            .map((u) => (
                            <tr key={u.id} className="border-b border-gray-200 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                            <td className="py-3 px-5">
                                <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                                    {u.name.charAt(0)}
                                </div>
                                <div>
                                    <div className="font-medium text-sm text-gray-900 dark:text-white">{u.name}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{u.phone}</div>
                                </div>
                                </div>
                            </td>
                            <td className="py-3 px-3">
                                <span className="font-mono text-xs bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">{u.id}</span>
                            </td>
                            <td className="py-3 px-3"><Badge status={u.kyc} /></td>
                            <td className="py-3 px-3"><Badge status={u.bank} /></td>
                            <td className="py-3 px-3"><Badge status={u.status} /></td>
                            
                            <td className="py-3 px-5">
                                {/* Replaced ActionBtn with a direct link to All_Customers */}
                                <Link
                                to="/dashboard/admin/all_customers"
                                className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10 hover:bg-violet-100 dark:hover:bg-violet-500/20 border border-violet-200 dark:border-violet-500/20 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                Go to Customers <ArrowUpRight size={14} />
                                </Link>
                            </td>
                            </tr>
                        ))
                        ) : (
                        <tr>
                            <td colSpan={6} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No members found.</td>
                        </tr>
                        )}
                    </tbody>
                    </table>
                </div>
                
                {/* Footer banner linking to the full list */}
                <div className="px-5 py-3 border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5 flex justify-between items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing 5 most recent members
                    </span>
                    <Link to="/dashboard/admin/all_customers" className="text-xs font-semibold text-primary hover:underline">
                    View All Directory &rarr;
                    </Link>
                </div>
                </div>

            {/* ── Recent Payments Preview Table ── */}
                <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl shadow-md dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] overflow-hidden flex flex-col">
                <div className="p-5 pb-0">
                    <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <CreditCard size={16} className="text-primary" />
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent Payments</h2>
                    </div>
                    <div className="flex gap-1.5">
                        {["all", "pending", "approved"].map((t) => (
                        <button
                            key={t}
                            onClick={() => setPayTab(t)}
                            className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors capitalize ${
                            payTab === t 
                                ? "bg-primary text-primary-foreground" 
                                : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            }`}
                        >
                            {t}
                        </button>
                        ))}
                    </div>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5">
                        <th className="text-left py-2.5 px-5 font-medium text-gray-500 dark:text-gray-400 text-xs">Member</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Type</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Amount</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Method</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Status</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Date</th>
                        <th className="text-left py-2.5 px-5 font-medium text-gray-500 dark:text-gray-400 text-xs">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayments.length > 0 ? (
                        filteredPayments
                            // 1. Sort by newest submission date
                            .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                            // 2. Take only the top 5
                            .slice(0, 5)
                            .map((p) => {
                            // Determine row background based on pending status
                            const isPending = p.status === "pending";
                            const rowClass = isPending 
                                ? "bg-amber-50/50 dark:bg-amber-500/10 hover:bg-amber-50 dark:hover:bg-amber-500/20" 
                                : "hover:bg-gray-50 dark:hover:bg-white/5";

                            return (
                                <tr key={p.id} className={`border-b border-gray-200 dark:border-white/5 last:border-0 transition-colors ${rowClass}`}>
                                <td className="py-3 px-5">
                                    <div className="font-medium text-sm text-gray-900 dark:text-white">{p.name}</div>
                                    <div className="font-mono text-xs text-gray-500 dark:text-gray-400">{p.transaction}</div>
                                </td>
                                <td className="py-3 px-3">
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-md capitalize ${
                                    p.type === "joining" 
                                        ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400" 
                                        : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                    }`}>
                                    {p.type}
                                    </span>
                                </td>
                                <td className="py-3 px-3 font-semibold text-gray-900 dark:text-white">
                                    ₹{fmtINR(parseFloat(p.amount))}
                                </td>
                                <td className="py-3 px-3">
                                    <span className="text-xs bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-md font-medium">
                                    {p.method || "UPI"}
                                    </span>
                                </td>
                                <td className="py-3 px-3"><Badge status={p.status} /></td>
                                <td className="py-3 px-3 text-xs text-gray-500 dark:text-gray-400">
                                    {fmtDate(p.submitted_at, { day: "numeric", month: "short" })}
                                </td>
                                <td className="py-3 px-5">
                                    {/* Replaced Approve/Reject buttons with a direct link to All_Payments */}
                                    <Link
                                    to="/dashboard/admin/all_payments"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 border border-blue-200 dark:border-blue-500/20 px-3 py-1.5 rounded-lg transition-colors"
                                    >
                                    Go to Payments <ArrowUpRight size={14} />
                                    </Link>
                                </td>
                                </tr>
                            );
                            })
                        ) : (
                        <tr>
                            <td colSpan={7} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No payments found.</td>
                        </tr>
                        )}
                    </tbody>
                    </table>
                </div>
                
                {/* Footer banner linking to the full list */}
                <div className="px-5 py-3 border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5 flex justify-between items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing 5 most recent payments
                    </span>
                    <Link to="/dashboard/admin/all_payments" className="text-xs font-semibold text-primary hover:underline">
                    View All Payments &rarr;
                    </Link>
                </div>
                </div>

            {/* ── Recent KYC Verification Preview Table ── */}
                <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl shadow-md dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] overflow-hidden flex flex-col">
                <div className="p-5 pb-0">
                    <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Shield size={16} className="text-primary" />
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent KYC Submissions</h2>
                    </div>
                    <div className="flex gap-1.5">
                        {["all", "pending", "approved"].map((t) => (
                        <button
                            key={t}
                            onClick={() => setKycTab(t)}
                            className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors capitalize ${
                            kycTab === t 
                                ? "bg-primary text-primary-foreground" 
                                : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            }`}
                        >
                            {t}
                        </button>
                        ))}
                    </div>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5">
                        <th className="text-left py-2.5 px-5 font-medium text-gray-500 dark:text-gray-400 text-xs">Member</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Aadhaar</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">PAN</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Submitted</th>
                        <th className="text-left py-2.5 px-3 font-medium text-gray-500 dark:text-gray-400 text-xs">Status</th>
                        <th className="text-left py-2.5 px-5 font-medium text-gray-500 dark:text-gray-400 text-xs">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredKyc.length > 0 ? (
                        filteredKyc
                            // 1. Sort by newest submission date
                            .sort((a, b) => new Date(b.submitted_on) - new Date(a.submitted_on))
                            // 2. Take only the top 5
                            .slice(0, 5)
                            .map((k) => {
                            // Determine row background based on pending status (Blue glow for KYC)
                            const isPending = k.status === "pending";
                            const rowClass = isPending 
                                ? "bg-blue-50/50 dark:bg-blue-500/10 hover:bg-blue-50 dark:hover:bg-blue-500/20" 
                                : "hover:bg-gray-50 dark:hover:bg-white/5";

                            return (
                                <tr key={k.id} className={`border-b border-gray-200 dark:border-white/5 last:border-0 transition-colors ${rowClass}`}>
                                <td className="py-3 px-5">
                                    <div className="font-medium text-sm text-gray-900 dark:text-white">{k.name}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{k.phone}</div>
                                </td>
                                <td className="py-3 px-3 font-mono text-xs text-gray-600 dark:text-gray-300">
                                    XXXX XXXX {k.aadhar.slice(-4)}
                                </td>
                                <td className="py-3 px-3 font-mono text-xs text-gray-600 dark:text-gray-300">
                                    {k.pan}
                                </td>
                                <td className="py-3 px-3 text-xs text-gray-500 dark:text-gray-400">
                                    {fmtDate(k.submitted_on, { day: "numeric", month: "short" })}
                                </td>
                                <td className="py-3 px-3"><Badge status={k.status} /></td>
                                <td className="py-3 px-5">
                                    {/* Replaced View/Approve/Reject buttons with a direct link to All_Kyc */}
                                    <Link
                                    to="/dashboard/admin/all_kyc"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-500/10 hover:bg-teal-100 dark:hover:bg-teal-500/20 border border-teal-200 dark:border-teal-500/20 px-3 py-1.5 rounded-lg transition-colors"
                                    >
                                    Go to KYC <ArrowUpRight size={14} />
                                    </Link>
                                </td>
                                </tr>
                            );
                            })
                        ) : (
                        <tr>
                            <td colSpan={6} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No KYC records found.</td>
                        </tr>
                        )}
                    </tbody>
                    </table>
                </div>

                {/* Footer banner linking to the full list */}
                <div className="px-5 py-3 border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/5 flex justify-between items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing 5 most recent submissions
                    </span>
                    <Link to="/dashboard/admin/all_kyc" className="text-xs font-semibold text-primary hover:underline">
                    View All KYC &rarr;
                    </Link>
                </div>
                </div>

          </div>{/* end left column */}

          {/* ════ RIGHT COLUMN ════ */}
          <div className="flex flex-col gap-6">

            {/* Revenue Ring */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
            <SectionHeading icon={<DollarSign size={16} />} title="Revenue Overview" />
            
            <div className="flex items-center justify-center py-3">
                <div className="relative">
                <svg width="160" height="160" viewBox="0 0 160 160">
                    
                    {/* Outer Revenue Track (Background) */}
                    {/* CHANGED: Removed stroke="hsl..." and added explicit stroke classes */}
                    <circle cx="80" cy="80" r="64" fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="16" />
                    <circle
                    cx="80" cy="80" r="64" fill="none" stroke="#6d5cf7" strokeWidth="16"
                    strokeDasharray={`${C_REV * revRatio} ${C_REV * (1 - revRatio)}`}
                    strokeLinecap="round" transform="rotate(-90 80 80)"
                    />
                    
                    {/* Inner Commission Track (Background) */}
                    {/* CHANGED: Removed stroke="hsl..." and added explicit stroke classes */}
                    <circle cx="80" cy="80" r="48" fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="10" />
                    <circle
                    cx="80" cy="80" r="48" fill="none" stroke="#10b981" strokeWidth="10"
                    strokeDasharray={`${2 * Math.PI * 48 * comRatio} ${2 * Math.PI * 48 * (1 - comRatio)}`}
                    strokeLinecap="round" transform="rotate(-90 80 80)"
                    />
                </svg>
                
                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    ₹{totalRevenue >= 1000 ? `${(totalRevenue / 1000).toFixed(0)}k` : fmtINR(totalRevenue)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Revenue</div>
                </div>
                </div>
            </div>
            
            {/* Legend */}
            <div className="flex justify-center gap-6 text-xs mb-4 text-gray-600 dark:text-gray-400 font-medium">
                <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" /> Total Revenue
                </span>
                <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Commission
                </span>
            </div>
            
            {/* Summary Boxes */}
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/20 p-3 text-center transition-colors">
                <div className="text-lg font-bold text-violet-700 dark:text-violet-400">₹{fmtINR(totalRevenue)}</div>
                <div className="text-xs text-violet-500 dark:text-violet-400/80 mt-0.5">Total Collected</div>
                </div>
                
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-3 text-center transition-colors">
                <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">₹{fmtINR(earnings.balance)}</div>
                <div className="text-xs text-emerald-500 dark:text-emerald-400/80 mt-0.5">Commission</div>
                </div>
            </div>
            </div>
            {/* KYC Status Breakdown */}
                    <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
                    <SectionHeading icon={<BadgeCheck size={16} />} title="KYC Status Breakdown" />
                    
                    <div className="flex items-center justify-center py-3">
                        <div className="relative">
                        <svg width="160" height="160" viewBox="0 0 160 160">
                            {/* Outer Ring Background */}
                            <circle cx="80" cy="80" r="64" fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="16" />
                            <circle
                            cx="80" cy="80" r="64" fill="none" stroke="#10b981" strokeWidth="16"
                            strokeDasharray={`${2 * Math.PI * 64 * (kycList.length ? kycList.filter(k => k.status === "approved").length / kycList.length : 0)} ${2 * Math.PI * 64}`}
                            strokeLinecap="round" transform="rotate(-90 80 80)"
                            />
                            
                            {/* Inner Ring Background */}
                            <circle cx="80" cy="80" r="48" fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="10" />
                            <circle
                            cx="80" cy="80" r="48" fill="none" stroke="#f59e0b" strokeWidth="10"
                            strokeDasharray={`${2 * Math.PI * 48 * (kycList.length ? pendingKyc / kycList.length : 0)} ${2 * Math.PI * 48}`}
                            strokeLinecap="round" transform="rotate(-90 80 80)"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {kycList.filter(k => k.status === "approved").length}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Approved</div>
                        </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-center gap-4 text-xs mb-3 text-gray-600 dark:text-gray-400 font-medium">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Approved</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /> Pending</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 transition-colors">
                        <div className="text-base font-bold text-emerald-700 dark:text-emerald-400">{kycList.filter(k => k.status === "approved").length}</div>
                        <div className="text-xs text-emerald-500 dark:text-emerald-400/80">Approved</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 transition-colors">
                        <div className="text-base font-bold text-amber-700 dark:text-amber-400">{pendingKyc}</div>
                        <div className="text-xs text-amber-500 dark:text-amber-400/80">Pending</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 transition-colors">
                        <div className="text-base font-bold text-red-700 dark:text-red-400">{kycList.filter(k => k.status === "rejected").length}</div>
                        <div className="text-xs text-red-500 dark:text-red-400/80">Rejected</div>
                        </div>
                    </div>
                    </div>

                    {/* Payment Type Split */}
                    <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
                    <SectionHeading icon={<Activity size={16} />} title="Payment Type Split" />
                    {(() => {
                        const joiningCount     = payments.filter(p => p.type === "joining").length;
                        const installmentCount = payments.filter(p => p.type === "installment").length;
                        const total            = payments.length || 1;
                        return (
                        <>
                            <div className="flex items-center justify-center py-3">
                            <div className="relative">
                                <svg width="160" height="160" viewBox="0 0 160 160">
                                {/* Outer Ring Background */}
                                <circle cx="80" cy="80" r="64" fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="16" />
                                <circle
                                    cx="80" cy="80" r="64" fill="none" stroke="#6d5cf7" strokeWidth="16"
                                    strokeDasharray={`${2 * Math.PI * 64 * (joiningCount / total)} ${2 * Math.PI * 64 * (1 - joiningCount / total)}`}
                                    strokeLinecap="round" transform="rotate(-90 80 80)"
                                />
                                
                                {/* Inner Ring Background */}
                                <circle cx="80" cy="80" r="48" fill="none" className="stroke-gray-200 dark:stroke-slate-700" strokeWidth="10" />
                                <circle
                                    cx="80" cy="80" r="48" fill="none" stroke="#10b981" strokeWidth="10"
                                    strokeDasharray={`${2 * Math.PI * 48 * (installmentCount / total)} ${2 * Math.PI * 48 * (1 - installmentCount / total)}`}
                                    strokeLinecap="round" transform="rotate(-90 80 80)"
                                />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">{payments.length}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Payments</div>
                                </div>
                            </div>
                            </div>
                            
                            <div className="flex justify-center gap-4 text-xs mb-3 text-gray-600 dark:text-gray-400 font-medium">
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" /> Joining</span>
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Installment</span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                            <div className="text-center p-2.5 rounded-xl bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/20 transition-colors">
                                <div className="text-lg font-bold text-violet-700 dark:text-violet-400">{joiningCount}</div>
                                <div className="text-xs text-violet-500 dark:text-violet-400/80">Joining Fees</div>
                            </div>
                            <div className="text-center p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 transition-colors">
                                <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{installmentCount}</div>
                                <div className="text-xs text-emerald-500 dark:text-emerald-400/80">Installments</div>
                            </div>
                            </div>
                        </>
                        );
                    })()}
                    </div>

           {/* Bank Accounts Preview */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
            <SectionHeading icon={<Banknote size={16} />} title="Recent Bank Submissions" />
            <div className="flex flex-col gap-3">
                {bankList.length > 0 ? (
                bankList
                    .sort((a, b) => new Date(b.created_at || b.id) - new Date(a.created_at || a.id)) // Sort if date exists
                    .slice(0, 5)
                    .map((b) => {
                    const isPending = b.status === "pending";
                    const rowClass = isPending 
                        ? "border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/10 hover:dark:bg-amber-500/20" 
                        : "border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/5 hover:dark:bg-white/10";

                    return (
                        <div key={b.id} className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${rowClass}`}>
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                            {b.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{b.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">XXXX {b.account.slice(-4)} · {b.ifsc}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <Badge status={b.status} />
                        </div>
                        </div>
                    );
                    })
                ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No bank accounts yet.</p>
                )}
            </div>
            
            {/* Link to full bank directory */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/5 flex justify-center">
                <Link
                to="/dashboard/admin/all_bank_details"
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 border border-blue-200 dark:border-blue-500/20 px-4 py-2 rounded-lg transition-colors w-full justify-center"
                >
                Manage All Bank Accounts <ArrowUpRight size={14} />
                </Link>
            </div>
            </div>

            {/* Admin Profile Card */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
            <SectionHeading icon={<UserCheck size={16} />} title="Admin Profile" />
            
            {/* We need to update the InfoRow component to support dark mode text too! */}
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/5">
                <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{profile.name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/5">
                <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[150px] sm:max-w-none">{profile.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/5">
                <span className="text-sm text-gray-500 dark:text-gray-400">Phone</span>
                <span className="text-sm font-medium font-mono text-gray-900 dark:text-white">{profile.phone}</span>
            </div>
            <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Location</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{profile.city}, {profile.state}</span>
            </div>

            <div className="mt-3 flex gap-2 flex-wrap">
                <Badge status="approved"  label="KYC Verified" />
                <Badge status="completed" label={profile.membership} />
            </div>
            </div>

            {/* Quick Admin Actions */}
            <div className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-white/5 rounded-xl p-5 shadow-sm dark:shadow-[0_0_15px_rgba(255,255,255,0.03)] hover:shadow-md transition-all duration-200">
            <SectionHeading icon={<Activity size={16} />} title="Quick Actions" />
            <div className="flex flex-col gap-2.5">
                {[
                { 
                    label: "Add New Member",      
                    icon: <Users size={14} />, 
                    color: "text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/20 hover:bg-indigo-100 dark:hover:bg-indigo-500/20",
                    to: "/dashboard/admin/all_customers"
                },
                { 
                    label: "Verify Pending KYC",  
                    icon: <Shield size={14} />, 
                    color: "text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/20 hover:bg-teal-100 dark:hover:bg-teal-500/20",
                    to: "/dashboard/admin/all_kyc"
                },
                { 
                    label: "Approve Payments",    
                    icon: <CheckCheck size={14} />, 
                    color: "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 hover:bg-emerald-100 dark:hover:bg-emerald-500/20",
                    to: "/dashboard/admin/all_payments"
                },
                { 
                    label: "Manage Banks", 
                    icon: <Banknote size={14} />, 
                    color: "text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20",
                    to: "/dashboard/admin/all_bank_details"
                },
                ].map((a) => (
                <Link 
                    key={a.label} 
                    to={a.to}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${a.color}`}
                >
                    <span className="flex items-center gap-3">{a.icon}{a.label}</span>
                    <ChevronRight size={14} className="opacity-70" />
                </Link>
                ))}
            </div>
            </div>

          </div>{/* end right column */}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground pb-4">
          Admin ID: {profile.id} &nbsp;|&nbsp; {profile.email} &nbsp;|&nbsp; Last synced: {new Date().toLocaleDateString("en-IN")}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;